import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import {
  createHomeSectionBanner,
  deleteHomeSectionBanner,
  listHomeSectionBanners,
  toggleHomeSectionBanner,
  updateHomeSectionBanner,
} from '../api/bannersApi';

const SECTION_OPTIONS = [
  { value: 'curated', label: 'Curated' },
  { value: 'featured', label: 'Featured' },
  { value: 'popular', label: 'Popular' },
  { value: 'trending', label: 'Trending' },
];

const TYPE_OPTIONS = [
  { value: 'TOP_HORIZONTAL', label: 'Top (Horizontal)' },
  { value: 'SIDE_VERTICAL', label: 'Side (Vertical)' },
  { value: 'SECTION_HEADER', label: 'Section header' },
];

const DIMENSIONS = {
  TOP_HORIZONTAL: '1600x420',
  SIDE_VERTICAL: '520x760',
  SECTION_HEADER: 'No image',
};

const requiresImage = (bannerType) => bannerType === 'TOP_HORIZONTAL' || bannerType === 'SIDE_VERTICAL';
const SECTION_LABELS = {
  ...Object.fromEntries(SECTION_OPTIONS.map((opt) => [opt.value, opt.label])),
  destinations: 'Destinations',
  destination: 'Destinations',
};

const validateFile = (file) => {
  if (!file) return '';
  const okType = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);
  if (!okType) return 'Only JPG, PNG, or WebP images are allowed.';
  const max = 2 * 1024 * 1024;
  if (file.size > max) return 'File too large. Max 2MB.';
  return '';
};

export default function HomeSectionBanners({ token }) {
  const [sectionKey, setSectionKey] = useState('curated');
  const [bannerType, setBannerType] = useState('TOP_HORIZONTAL');

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [includeDestinations, setIncludeDestinations] = useState(false);

  const [headerId, setHeaderId] = useState(null);
  const [headerLoading, setHeaderLoading] = useState(false);
  const [headerSaving, setHeaderSaving] = useState(false);
  const [headerError, setHeaderError] = useState('');
  const [headerForm, setHeaderForm] = useState({
    eyebrow: '',
    title: '',
    subtitle: '',
    is_active: true,
  });

  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    section_key: 'curated',
    banner_type: 'TOP_HORIZONTAL',
    eyebrow: '',
    title: '',
    subtitle: '',
    cta_text: '',
    cta_link: '',
    is_active: true,
    sort_order: 0,
    image_url: '',
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const requiredSizeText = useMemo(() => DIMENSIONS[bannerType] || '', [bannerType]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const rows = await listHomeSectionBanners({ banner_type: bannerType }, token);
      const list = Array.isArray(rows) ? rows : [];
      const allowedSections = new Set(SECTION_OPTIONS.map((opt) => opt.value));
      if (includeDestinations) {
        allowedSections.add('destinations');
        allowedSections.add('destination');
      }
      const filtered = list.filter((row) => {
        const key = String(row.section_key || row.sectionKey || '').toLowerCase();
        return allowedSections.has(key);
      });
      const ordered = [...filtered].sort((a, b) => {
        const aKey = String(a.section_key || a.sectionKey || '');
        const bKey = String(b.section_key || b.sectionKey || '');
        if (aKey !== bKey) return aKey.localeCompare(bKey);
        return Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0);
      });
      setItems(ordered);
    } catch (err) {
      setItems([]);
      setError(err.message || 'Unable to load banners.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadHeader();
    // keep create-form aligned with current filters unless editing
    setForm((prev) =>
      editingId
        ? prev
        : {
            ...prev,
            section_key: sectionKey,
            banner_type: bannerType,
          }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionKey, bannerType, includeDestinations]);

  const loadHeader = async () => {
    setHeaderLoading(true);
    setHeaderError('');
    try {
      const rows = await listHomeSectionBanners({ section_key: sectionKey, banner_type: 'SECTION_HEADER' }, token);
      const list = Array.isArray(rows) ? rows : rows?.value || [];
      const header = list?.[0] || null;
      if (!header) {
        setHeaderId(null);
        setHeaderForm({ eyebrow: '', title: '', subtitle: '', is_active: true });
      } else {
        setHeaderId(header.id);
        setHeaderForm({
          eyebrow: header.eyebrow || '',
          title: header.title || '',
          subtitle: header.subtitle || '',
          is_active: !!header.is_active,
        });
      }
    } catch (err) {
      setHeaderId(null);
      setHeaderForm({ eyebrow: '', title: '', subtitle: '', is_active: true });
      setHeaderError(err.message || 'Unable to load section header.');
    } finally {
      setHeaderLoading(false);
    }
  };

  const saveHeader = async () => {
    setHeaderError('');
    const payload = {
      section_key: sectionKey,
      banner_type: 'SECTION_HEADER',
      eyebrow: headerForm.eyebrow,
      title: headerForm.title,
      subtitle: headerForm.subtitle,
      is_active: !!headerForm.is_active,
      sort_order: 0,
    };

    if (!String(payload.title || '').trim()) {
      setHeaderError('Title is required for Section header.');
      return;
    }

    setHeaderSaving(true);
    try {
      if (headerId) {
        await updateHomeSectionBanner(headerId, payload, null, token);
      } else {
        const created = await createHomeSectionBanner(payload, null, token);
        setHeaderId(created?.id ?? null);
      }
      await loadHeader();
    } catch (err) {
      setHeaderError(err.message || 'Unable to save section header.');
    } finally {
      setHeaderSaving(false);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFile(null);
    setForm({
      section_key: sectionKey,
      banner_type: bannerType,
      eyebrow: '',
      title: '',
      subtitle: '',
      cta_text: '',
      cta_link: '',
      is_active: true,
      sort_order: 0,
      image_url: '',
    });
    setError('');
  };

  const onEdit = (row) => {
    setEditingId(row.id);
    setFile(null);
    setForm({
      section_key: row.section_key,
      banner_type: row.banner_type,
      eyebrow: row.eyebrow || '',
      title: row.title || '',
      subtitle: row.subtitle || '',
      cta_text: row.cta_text || '',
      cta_link: row.cta_link || '',
      is_active: !!row.is_active,
      sort_order: Number(row.sort_order ?? 0),
      image_url: row.image_url || '',
    });
  };

  const onToggle = async (row) => {
    setError('');
    try {
      await toggleHomeSectionBanner(row.id, !row.is_active, token);
      await load();
    } catch (err) {
      setError(err.message || 'Unable to update status.');
    }
  };

  const onDelete = async (row) => {
    // eslint-disable-next-line no-alert
    const ok = window.confirm('Delete this banner?');
    if (!ok) return;
    setError('');
    try {
      await deleteHomeSectionBanner(row.id, token);
      setItems((prev) => prev.filter((item) => item.id !== row.id));
      if (editingId === row.id) resetForm();
    } catch (err) {
      setError(err.message || 'Unable to delete banner.');
    }
  };

  const onSave = async () => {
    setError('');

    if (!form.section_key || !form.banner_type) {
      setError('Section and banner type are required.');
      return;
    }

    if (form.banner_type === 'SECTION_HEADER' && !String(form.title || '').trim()) {
      setError('Title is required for Section header.');
      return;
    }

    const fileError = validateFile(file);
    if (fileError) {
      setError(fileError);
      return;
    }

    if (!editingId && requiresImage(form.banner_type) && !file) {
      setError('Image is required for Top and Side banners.');
      return;
    }

    const payload = {
      section_key: form.section_key,
      banner_type: form.banner_type,
      eyebrow: form.eyebrow,
      title: form.title,
      subtitle: form.subtitle,
      cta_text: form.cta_text,
      cta_link: form.cta_link,
      is_active: !!form.is_active,
      sort_order: Number(form.sort_order ?? 0),
    };

    setSaving(true);
    try {
      const saved = editingId
        ? await updateHomeSectionBanner(editingId, payload, file, token)
        : await createHomeSectionBanner(payload, file, token);
      resetForm();
      // Refresh list to keep ordering correct and show server-side changes.
      await load();
      // Optimistic insert/update is fine, but refresh keeps logic simple.
      return saved;
    } catch (err) {
      setError(err.message || 'Unable to save banner.');
    } finally {
      setSaving(false);
    }
  };

  const previewUrl = useMemo(() => {
    if (file) return URL.createObjectURL(file);
    return resolveAssetUrl(form.image_url);
  }, [file, form.image_url]);

  useEffect(() => {
    return () => {
      if (file) {
        try {
          URL.revokeObjectURL(previewUrl);
        } catch (_) {
          // ignore
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  return (
    <div className="p-3 p-md-4">
      <div className="d-flex align-items-start gap-2 flex-wrap mb-3">
        <div className="me-auto">
          <div className="h5 mb-0">Home page section banners</div>
          <div className="text-muted small">
            Manage only: section header text, top (wide) banner, and side (vertical) promo per section.
          </div>
        </div>
        <Button variant="outline-secondary" onClick={load} disabled={loading}>
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" /> Refresh
            </>
          ) : (
            'Refresh'
          )}
        </Button>
      </div>

      {error ? (
        <Alert variant="danger" className="py-2">
          {error}
        </Alert>
      ) : null}
      {headerError ? (
        <Alert variant="danger" className="py-2">
          {headerError}
        </Alert>
      ) : null}

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={6}>
              <Form.Label className="small text-muted mb-1">Section</Form.Label>
              <Form.Select value={sectionKey} onChange={(e) => setSectionKey(e.target.value)}>
                {SECTION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label className="small text-muted mb-1">Banner type</Form.Label>
              <Form.Select value={bannerType} onChange={(e) => setBannerType(e.target.value)}>
                {TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
              <div className="text-muted small mt-1">Required size: {requiredSizeText}</div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <div className="fw-semibold">Section header</div>
              <div className="text-muted small">
                Edit the main heading and subheading shown above the cards. The main heading replaces the default section name (e.g. Featured).
              </div>
            </div>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={loadHeader}
              disabled={headerLoading}
            >
              {headerLoading ? (
                <>
                  <Spinner size="sm" className="me-2" /> Refresh
                </>
              ) : (
                'Refresh'
              )}
            </Button>
          </div>

          <Row className="g-2 mt-2">
            <Col md={12}>
              <Form.Label className="small text-muted mb-1">Small label (optional)</Form.Label>
              <Form.Control
                value={headerForm.eyebrow}
                onChange={(e) => setHeaderForm((p) => ({ ...p, eyebrow: e.target.value }))}
                placeholder="e.g. Featured"
              />
            </Col>
            <Col md={12}>
              <Form.Label className="small text-muted mb-1">Main heading (replaces Featured/Curated)</Form.Label>
              <Form.Control
                value={headerForm.title}
                onChange={(e) => setHeaderForm((p) => ({ ...p, title: e.target.value }))}
                placeholder="Section heading"
              />
            </Col>
            <Col md={12}>
              <Form.Label className="small text-muted mb-1">Subheading</Form.Label>
              <Form.Control
                value={headerForm.subtitle}
                onChange={(e) => setHeaderForm((p) => ({ ...p, subtitle: e.target.value }))}
                placeholder="Section subheading"
              />
            </Col>
            <Col md={6}>
              <Form.Label className="small text-muted mb-1">Active</Form.Label>
              <Form.Check
                type="switch"
                id="header-active"
                checked={!!headerForm.is_active}
                onChange={(e) => setHeaderForm((p) => ({ ...p, is_active: e.target.checked }))}
              />
            </Col>
            <Col md={12} className="pt-1">
              <Button variant="primary" onClick={saveHeader} disabled={headerSaving}>
                {headerSaving ? (
                  <>
                    <Spinner size="sm" className="me-2" /> Saving
                  </>
                ) : (
                  'Save header'
                )}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row className="g-3">
        <Col lg={5}>
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="fw-semibold">{editingId ? 'Edit banner' : 'Add banner'}</div>
                  <div className="text-muted small">Applies to {sectionKey}.</div>
                </div>
                {editingId ? (
                  <Button size="sm" variant="outline-secondary" onClick={resetForm}>
                    Cancel
                  </Button>
                ) : null}
              </div>

              <Form className="mt-3" onSubmit={(e) => e.preventDefault()}>
                <Row className="g-2">
                  <Col md={6}>
                    <Form.Label className="small text-muted mb-1">Section</Form.Label>
                    <Form.Select
                      value={form.section_key}
                      onChange={(e) => setForm((p) => ({ ...p, section_key: e.target.value }))}
                    >
                      {SECTION_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={6}>
                    <Form.Label className="small text-muted mb-1">Type</Form.Label>
                    <Form.Select
                      value={form.banner_type}
                      onChange={(e) => {
                        const next = e.target.value;
                        setFile(null);
                        setForm((p) => ({
                          ...p,
                          banner_type: next,
                          ...(next === 'SECTION_HEADER' ? { image_url: '' } : {}),
                        }));
                      }}
                    >
                      {TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Form.Select>
                    <div className="text-muted small mt-1">Required size: {DIMENSIONS[form.banner_type] || ''}</div>
                  </Col>

                  {requiresImage(form.banner_type) ? (
                    <Col md={12}>
                      <Form.Label className="small text-muted mb-1">Image</Form.Label>
                      <Form.Control
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={(e) => {
                          const next = e.target.files?.[0] || null;
                          setFile(next);
                        }}
                      />
                      <div className="text-muted small mt-1">Allowed: JPG/PNG/WebP, max 2MB.</div>
                      {previewUrl ? (
                        <div className="mt-2">
                          <img
                            src={previewUrl}
                            alt="Preview"
                            style={{
                              width: '100%',
                              maxHeight: 220,
                              objectFit: 'cover',
                              borderRadius: 12,
                              border: '1px solid rgba(0,0,0,0.08)',
                            }}
                          />
                        </div>
                      ) : null}
                    </Col>
                  ) : null}

                  <Col md={12}>
                    <Form.Label className="small text-muted mb-1">
                      {form.banner_type === 'SECTION_HEADER' ? 'Section label (optional)' : 'Kicker (optional)'}
                    </Form.Label>
                    <Form.Control
                      value={form.eyebrow}
                      onChange={(e) => setForm((p) => ({ ...p, eyebrow: e.target.value }))}
                      placeholder={form.banner_type === 'SECTION_HEADER' ? 'e.g. Trending' : 'e.g. Limited offer'}
                    />
                  </Col>

                  <Col md={12}>
                    <Form.Label className="small text-muted mb-1">Title</Form.Label>
                    <Form.Control value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} />
                  </Col>
                  <Col md={12}>
                    <Form.Label className="small text-muted mb-1">Subtitle</Form.Label>
                    <Form.Control
                      value={form.subtitle}
                      onChange={(e) => setForm((p) => ({ ...p, subtitle: e.target.value }))}
                    />
                  </Col>

                  {requiresImage(form.banner_type) ? (
                    <>
                      <Col md={12}>
                        <Form.Label className="small text-muted mb-1">CTA text (optional)</Form.Label>
                        <Form.Control
                          value={form.cta_text}
                          onChange={(e) => setForm((p) => ({ ...p, cta_text: e.target.value }))}
                          placeholder="e.g. Explore now"
                        />
                      </Col>
                      <Col md={12}>
                        <Form.Label className="small text-muted mb-1">CTA link (optional)</Form.Label>
                        <Form.Control
                          value={form.cta_link}
                          onChange={(e) => setForm((p) => ({ ...p, cta_link: e.target.value }))}
                          placeholder="/stays or https://..."
                        />
                      </Col>
                    </>
                  ) : null}

                  <Col md={6}>
                    <Form.Label className="small text-muted mb-1">Active</Form.Label>
                    <Form.Check
                      type="switch"
                      id="banner-active"
                      checked={!!form.is_active}
                      onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                      label={form.is_active ? 'Active' : 'Inactive'}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label className="small text-muted mb-1">Sort order</Form.Label>
                    <Form.Control
                      type="number"
                      value={Number(form.sort_order ?? 0)}
                      onChange={(e) => setForm((p) => ({ ...p, sort_order: Number(e.target.value || 0) }))}
                    />
                  </Col>

                  <Col md={12} className="d-flex gap-2 pt-2">
                    <Button variant="primary" onClick={onSave} disabled={saving}>
                      {saving ? (
                        <>
                          <Spinner size="sm" className="me-2" /> Saving
                        </>
                      ) : (
                        'Save'
                      )}
                    </Button>
                    <Button variant="outline-secondary" onClick={resetForm} disabled={saving}>
                      Reset
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={7}>
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                <div className="fw-semibold">Banners</div>
                <Form.Check
                  type="switch"
                  id="include-destinations"
                  label="Include destinations"
                  checked={includeDestinations}
                  onChange={(e) => setIncludeDestinations(e.target.checked)}
                />
                <div className="text-muted small">
                  {includeDestinations ? 'Home + destinations' : 'Home sections only'} | {bannerType} | {items.length} item(s)
                </div>
              </div>
              <div className="text-muted small mb-2">Required size: {requiredSizeText}</div>
              <div className="text-muted small mb-2">
                {includeDestinations
                  ? 'Showing all home sections plus destinations for the selected banner type.'
                  : 'Showing home sections only for the selected banner type.'}
              </div>

              <div style={{ overflowX: 'auto' }}>
                <Table size="sm" bordered hover className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: 92 }}>Preview</th>
                      <th style={{ width: 120 }}>Section</th>
                      <th>Title</th>
                      <th>Subtitle</th>
                      <th style={{ width: 90 }}>Active</th>
                      <th style={{ width: 70 }}>Sort</th>
                      <th style={{ width: 220 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row) => (
                      <tr key={row.id}>
                        <td>
                          {row.image_url ? (
                            <img
                              src={resolveAssetUrl(row.image_url)}
                              alt={row.title || 'Banner'}
                              style={{
                                width: 76,
                                height: row.banner_type === 'SIDE_VERTICAL' ? 76 : 44,
                                objectFit: 'cover',
                                borderRadius: 10,
                                border: '1px solid rgba(0,0,0,0.08)',
                              }}
                              loading="lazy"
                            />
                          ) : (
                            <span className="text-muted small">No image</span>
                          )}
                        </td>
                        <td className="text-muted small">
                          {SECTION_LABELS[row.section_key] || row.section_key || '-'}
                        </td>
                        <td className="text-truncate" style={{ maxWidth: 220 }}>
                          {row.title || <span className="text-muted">(Untitled)</span>}
                          <div className="mt-1">
                            <Badge bg="light" text="dark" className="border">
                              {row.banner_type}
                            </Badge>{' '}
                            <span className="text-muted small">({DIMENSIONS[row.banner_type] || ''})</span>
                          </div>
                          {row.eyebrow ? (
                            <div className="text-muted small mt-1">Label: {row.eyebrow}</div>
                          ) : null}
                          {row.cta_text ? (
                            <div className="text-muted small mt-1">CTA: {row.cta_text}</div>
                          ) : null}
                        </td>
                        <td className="text-truncate" style={{ maxWidth: 240 }}>
                          {row.subtitle || <span className="text-muted">-</span>}
                        </td>
                        <td>
                          {row.is_active ? <Badge bg="success">Active</Badge> : <Badge bg="secondary">Inactive</Badge>}
                        </td>
                        <td>{Number(row.sort_order ?? 0)}</td>
                        <td className="text-nowrap">
                          <Button size="sm" variant="outline-primary" onClick={() => onEdit(row)}>
                            Edit
                          </Button>{' '}
                          <Button size="sm" variant="outline-secondary" onClick={() => onToggle(row)}>
                            Toggle
                          </Button>{' '}
                          <Button size="sm" variant="outline-danger" onClick={() => onDelete(row)}>
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {!loading && items.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center text-muted">
                          No banners yet.
                        </td>
                      </tr>
                    ) : null}
                    {loading ? (
                      <tr>
                        <td colSpan={6} className="text-center text-muted">
                          <Spinner size="sm" className="me-2" /> Loading...
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
